import type { DailyPlan } from "@/domain";

export const SESSION_TARGET_MINUTES = 15;
export const SESSION_MIN_TASKS = 3;
export const SESSION_MAX_TASKS = 4;
export const REST_REMINDER_INTERVAL_MS =
  SESSION_TARGET_MINUTES * 60 * 1_000;

export interface CreateDailySessionPlanInput {
  profileId: string;
  date: string;
  candidateLevelIds: readonly string[];
}

export interface DailySession {
  readonly plan: DailyPlan;
  readonly startedAtMs: number;
  readonly completedLevelIds: readonly string[];
  readonly acknowledgedRestIntervals: number;
}

export type TaskCompletionOutcome =
  | "completed"
  | "already-completed"
  | "not-in-plan";

export interface TaskCompletionResult {
  readonly session: DailySession;
  readonly outcome: TaskCompletionOutcome;
  readonly isSessionComplete: boolean;
}

export interface RestReminderStatus {
  readonly isDue: boolean;
  readonly reminderNumber: number;
  readonly dueAtMs: number;
}

export function createDailySessionPlan({
  profileId,
  date,
  candidateLevelIds
}: CreateDailySessionPlanInput): DailyPlan {
  const uniqueLevelIds = [...new Set(candidateLevelIds)];

  if (uniqueLevelIds.some((levelId) => levelId.length === 0)) {
    throw new TypeError("Session level IDs must not be empty");
  }

  if (uniqueLevelIds.length < SESSION_MIN_TASKS) {
    throw new RangeError(
      "A daily session needs at least " +
        SESSION_MIN_TASKS +
        " unique activities"
    );
  }

  return {
    profileId,
    date,
    levelIds: uniqueLevelIds.slice(0, SESSION_MAX_TASKS),
    targetMinutes: SESSION_TARGET_MINUTES
  };
}

export function startDailySession(
  plan: DailyPlan,
  startedAtMs: number
): DailySession {
  assertTimestamp(startedAtMs);
  assertDailySessionPlan(plan);

  return {
    plan: { ...plan, levelIds: [...plan.levelIds] },
    startedAtMs,
    completedLevelIds: [],
    acknowledgedRestIntervals: 0
  };
}

export function completeSessionTask(
  session: DailySession,
  levelId: string
): TaskCompletionResult {
  if (!session.plan.levelIds.includes(levelId)) {
    return unchangedCompletion(session, "not-in-plan");
  }

  if (session.completedLevelIds.includes(levelId)) {
    return unchangedCompletion(session, "already-completed");
  }

  const completedLevelIds = [...session.completedLevelIds, levelId];
  const updatedSession = { ...session, completedLevelIds };

  return {
    session: updatedSession,
    outcome: "completed",
    isSessionComplete: isDailySessionComplete(updatedSession)
  };
}

export function isDailySessionComplete(session: DailySession): boolean {
  return session.plan.levelIds.every((levelId) =>
    session.completedLevelIds.includes(levelId)
  );
}

export function getRestReminderStatus(
  session: DailySession,
  nowMs: number
): RestReminderStatus {
  assertTimestamp(nowMs);

  const reminderNumber = session.acknowledgedRestIntervals + 1;
  const dueAtMs =
    session.startedAtMs + reminderNumber * REST_REMINDER_INTERVAL_MS;

  return {
    isDue: nowMs >= dueAtMs,
    reminderNumber,
    dueAtMs
  };
}

export function acknowledgeRestReminder(
  session: DailySession,
  nowMs: number
): DailySession {
  assertTimestamp(nowMs);

  const elapsedMs = Math.max(0, nowMs - session.startedAtMs);
  const elapsedIntervals = Math.floor(
    elapsedMs / REST_REMINDER_INTERVAL_MS
  );

  if (elapsedIntervals <= session.acknowledgedRestIntervals) {
    return session;
  }

  return {
    ...session,
    acknowledgedRestIntervals: elapsedIntervals
  };
}

function assertDailySessionPlan(plan: DailyPlan): void {
  const uniqueLevelIds = new Set(plan.levelIds);

  if (
    plan.targetMinutes !== SESSION_TARGET_MINUTES ||
    plan.levelIds.length < SESSION_MIN_TASKS ||
    plan.levelIds.length > SESSION_MAX_TASKS ||
    uniqueLevelIds.size !== plan.levelIds.length ||
    plan.levelIds.some((levelId) => levelId.length === 0)
  ) {
    throw new RangeError("Invalid 15-minute daily session plan");
  }
}

function assertTimestamp(timestampMs: number): void {
  if (!Number.isFinite(timestampMs)) {
    throw new TypeError("Session timestamps must be finite numbers");
  }
}

function unchangedCompletion(
  session: DailySession,
  outcome: Exclude<TaskCompletionOutcome, "completed">
): TaskCompletionResult {
  return {
    session,
    outcome,
    isSessionComplete: isDailySessionComplete(session)
  };
}
